import React from "react";
import { formatTRY } from "../lib/mw";
import { Users, UserCheck, Star, Target, Trophy, TrendingUp, MapPin, Wallet, Truck, CalendarDays } from "lucide-react";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Cell } from "recharts";

const Stat = ({ icon: Icon, value, sub, color }) => (
  <div className="flex h-full items-center justify-between gap-2">
    <div className="min-w-0">
      <div className="truncate font-head text-2xl font-extrabold text-white xl:text-3xl">{value}</div>
      {sub && <div className="truncate text-[11px] text-slate-400">{sub}</div>}
    </div>
    <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg" style={{ background: `${color}22` }}><Icon className="h-5 w-5" style={{ color }} /></div>
  </div>
);

const Pills = ({ data, colors }) => (
  <div className="flex flex-wrap gap-1.5">
    {Object.keys(data || {}).length === 0 && <span className="text-xs text-slate-600">VERİ YOK</span>}
    {Object.entries(data || {}).map(([k, v]) => (
      <span key={k} className="rounded-full px-2.5 py-0.5 text-[11px] font-semibold" style={{ background: `${(colors && colors[k]) || "#3B82F6"}22`, color: (colors && colors[k]) || "#93c5fd" }}>{k} · {v}</span>
    ))}
  </div>
);

const SONUC = { KAZANILDI: "#10B981", KAYBEDİLDİ: "#EF4444", "DEVAM EDİYOR": "#F59E0B" };
const dash = "—";

export const WIDGETS = {
  genel: { t: "GENEL MÜŞTERİ", r: ({ ov }) => <Stat icon={Users} value={ov?.genel ?? dash} color="#3B82F6" /> },
  portfoy: { t: "PORTFÖY MÜŞTERİ", r: ({ ov }) => <Stat icon={UserCheck} value={ov?.portfoy ?? dash} color="#8B5CF6" /> },
  ozel: { t: "ÖZEL MÜŞTERİ", r: ({ ov }) => <Stat icon={Star} value={ov?.ozel ?? dash} color="#F59E0B" /> },
  toplam: { t: "TOPLAM MÜŞTERİ", r: ({ ov }) => <Stat icon={Users} value={ov?.total ?? dash} color="#06B6D4" /> },
  hedef: { t: "TOPLAM HEDEF CİRO", r: ({ ov }) => <Stat icon={Target} value={ov ? formatTRY(ov.total_hedef) : dash} color="#3B82F6" /> },
  gercek: { t: "GERÇEKLEŞEN CİRO", r: ({ ov }) => <Stat icon={TrendingUp} value={ov ? formatTRY(ov.total_gerceklesen) : dash} color="#10B981" /> },
  basari: { t: "BAŞARI ORANI", r: ({ ov }) => <Stat icon={Trophy} value={ov ? `%${ov.total_hedef ? Math.round((ov.total_gerceklesen / ov.total_hedef) * 100) : 0}` : dash} color="#F59E0B" /> },
  ilk_temas: { t: "İLK TEMAS", r: ({ ov }) => <Pills data={ov?.ilk_temas} colors={{ ARAMA: "#3B82F6", EMAIL: "#8B5CF6", MESAJ: "#10B981" }} /> },
  sonuc: { t: "SONUÇ DAĞILIMI", r: ({ ov }) => <Pills data={ov?.sonuc} colors={SONUC} /> },
  asama1: { t: "1. AŞAMA", r: ({ ov }) => <Pills data={ov?.asama1} /> },
  asama2: { t: "2. AŞAMA", r: ({ ov }) => <Pills data={ov?.asama2} /> },
  asama3: { t: "3. AŞAMA", r: ({ ov }) => <Pills data={ov?.asama3} /> },
  grafik: { t: "SONUÇ GRAFİĞİ", r: ({ ov }) => {
    const d = Object.entries(ov?.sonuc || {}).map(([name, value]) => ({ name, value }));
    return (
      <ResponsiveContainer width="100%" height="100%">
        <BarChart data={d}>
          <XAxis dataKey="name" tick={{ fill: "#94A3B8", fontSize: 10 }} />
          <YAxis tick={{ fill: "#94A3B8", fontSize: 10 }} allowDecimals={false} width={28} />
          <Tooltip contentStyle={{ background: "#1E293B", border: "1px solid #334155", color: "#fff" }} />
          <Bar dataKey="value" radius={[5, 5, 0, 0]}>{d.map((e, i) => <Cell key={i} fill={SONUC[e.name] || "#3B82F6"} />)}</Bar>
        </BarChart>
      </ResponsiveContainer>
    );
  } },
  il: { t: "İL BAZLI ÖZET", icon: MapPin, r: ({ loc }) => (
    <div className="grid grid-cols-1 gap-1.5 sm:grid-cols-2">
      {(loc || []).slice(0, 8).map((il) => (
        <div key={il.il} className="rounded-md border border-slate-800 bg-slate-800/40 px-2 py-1">
          <div className="flex items-center justify-between text-xs"><span className="font-semibold text-slate-100">{il.il}</span><span className="text-blue-300">{il.count}</span></div>
          <div className="flex justify-between text-[10px] text-slate-400"><span>H: {formatTRY(il.hedef)}</span><span className="text-emerald-400">G: {formatTRY(il.gerceklesen)}</span></div>
        </div>
      ))}
      {!(loc || []).length && <span className="text-xs text-slate-600">VERİ YOK</span>}
    </div>
  ) },
  kasa: { t: "KASA BAKİYESİ", r: ({ kasa }) => <Stat icon={Wallet} value={kasa ? formatTRY(kasa.GENEL || 0) : dash} sub={kasa ? Object.entries(kasa).filter(([k]) => k !== "GENEL").map(([k, v]) => `${k}: ${formatTRY(v)}`).join(" · ") : ""} color="#10B981" /> },
  kargo_bugun: { t: "BUGÜNKÜ KARGO", r: ({ cBugun }) => <Stat icon={Truck} value={cBugun ? `${cBugun.adet} ADET` : dash} sub={cBugun ? `YEKÜN ${formatTRY(cBugun.yekun)} · KALAN ${formatTRY(cBugun.kalan)}` : ""} color="#3B82F6" /> },
  kargo_ay: { t: "BU AY KARGO", r: ({ cAy }) => <Stat icon={CalendarDays} value={cAy ? `${cAy.adet} ADET` : dash} sub={cAy ? `YEKÜN ${formatTRY(cAy.yekun)} · KALAN ${formatTRY(cAy.kalan)}` : ""} color="#8B5CF6" /> },
};

export const DEFAULT_LAYOUT = [
  { id: "genel", w: 1, h: 1 }, { id: "portfoy", w: 1, h: 1 }, { id: "ozel", w: 1, h: 1 }, { id: "toplam", w: 1, h: 1 },
  { id: "hedef", w: 1, h: 1 }, { id: "gercek", w: 1, h: 1 }, { id: "basari", w: 1, h: 1 }, { id: "kasa", w: 1, h: 1 },
  { id: "ilk_temas", w: 1, h: 1 }, { id: "sonuc", w: 1, h: 1 }, { id: "grafik", w: 2, h: 2 },
  { id: "asama1", w: 1, h: 1 }, { id: "asama2", w: 1, h: 1 },
];
